import { useState } from 'react';
import { useCallback } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { MultiDetailSection } from 'src/modals/common';
import { DuplicateListingSection } from 'src/modals/ListingDetails/components/DuplicateListingSection/DuplicateListingSection';
import { EnableNativeSyncSection } from 'src/modals/ListingDetails/components/EnableNativeSyncSection/EnableNativeSyncSection';
import { ProofOfPurchaseSection } from 'src/modals/ListingDetails/components/ProofOfPurchaseSection/ProofOfPurchaseSection';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ListingDetailDataField,
  ListingDetails,
  ListingStatus,
  Marketplace,
} from 'src/WebApiController';
import { ListingClient } from 'src/WebApiController';

import { useClientSideMarketplaceListingStatus } from './MarketplaceBroadcastSection.hooks';
import { MarketplaceTable } from './MarketplaceTable';

export const MarketplaceBroadcastSection = ({
  listing,
  disabled,
  setLoading,
}: {
  listing: ListingDetails;
  disabled?: boolean;
  setLoading: (nextValue: boolean) => void;
}) => {
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();

  const [ignoreDup, setIgnoreDup] = useState(listing?.ignoreDup ?? false);

  const { showErrorDialog } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const [marketplaceListingStatus, setMarketplaceListingStatus] =
    useClientSideMarketplaceListingStatus(listing);

  const handleIgnoreDuplicateClick = useCallback(
    async (isChecked: boolean) => {
      setLoading(true);
      setIgnoreDup(isChecked);
      tryInvokeApi(
        async () => {
          const client = new ListingClient(activeAccountWebClientConfig);
          const success = await client.updateListingIgnoreDuplicate(
            listing?.id,
            isChecked
          );

          if (!success) {
            setIgnoreDup(!isChecked);
          } else if (listing) {
            // Refreshing the listing data to reflect the new ignore duplicate status
            // Necessary to make sure the disabled state is up to date in the UI
            setActivePosEntity(
              listing.id,
              listing.idOnMkp,
              true,
              [ListingDetailDataField.Basic],
              true
            );

            refreshExpandedListItems();

            const unbroadcastResult = await client.deleteMarketplaceListings(
              listing.id,
              []
            );
            if (unbroadcastResult) {
              const newStatuses = Object.keys(marketplaceListingStatus).reduce(
                (acc, marketplace) => {
                  acc[marketplace as Marketplace] = ListingStatus.Delisted;
                  return acc;
                },
                {} as Record<Marketplace, ListingStatus>
              );
              setMarketplaceListingStatus(newStatuses);
            }
          }
        },
        (error) => {
          showErrorDialog('ListingClient.updateListingIgnoreDuplicate', error, {
            trackErrorData: { ListingId: listing?.id },
          });
        },
        () => {
          setLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      listing,
      marketplaceListingStatus,
      refreshExpandedListItems,
      setActivePosEntity,
      setLoading,
      setMarketplaceListingStatus,
      showErrorDialog,
    ]
  );

  const isDuplicate = !!listing?.dupListingId && !ignoreDup;

  const isDisabled = disabled || isDuplicate;

  const sections = [
    <EnableNativeSyncSection key="1" listing={listing} disabled={disabled} />,
    <ProofOfPurchaseSection key="2" listing={listing} />,
    <DuplicateListingSection
      key="3"
      listing={listing}
      allowBroadcast={ignoreDup ?? false}
      handleClick={handleIgnoreDuplicateClick}
    />,
  ];

  return (
    <>
      <MultiDetailSection
        name={<Content id={ContentId.Broadcast} />}
        extraContent={sections}
      >
        <MarketplaceTable
          disabled={isDisabled}
          listing={listing}
          marketplaceListingStatus={marketplaceListingStatus}
          setMarketplaceListingStatus={setMarketplaceListingStatus}
        />
      </MultiDetailSection>
    </>
  );
};
